/**
 * A card may never take down the board, and the board may never take down the
 * chat (§4.2). Every level is wrapped in one of these, so a component that
 * throws costs its own frame and nothing else.
 *
 * `recover` (the chat): the frame is mounted afresh instead of staying empty — QA dev 30.10.2026, a
 * `removeChild` error during an answer left the whole feed blank until F5 (`errorRecovery.ts`).
 */

import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';

import { shouldRetry } from './errorRecovery';

type Props = {
  children: ReactNode;
  /** What replaces the subtree. `null` renders nothing at all. */
  fallback?: ReactNode;
  label?: string;
  /** Mount the subtree afresh after an error (`errorRecovery.shouldRetry`); past the limit, a button does. */
  recover?: boolean;
};

type State = { failed: boolean; generation: number };

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, generation: 0 };

  private retries: number[] = [];

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.label ?? 'ui'}]`, error, info.componentStack);
    if (!this.props.recover) return;
    const now = Date.now();
    if (!shouldRetry(this.retries, now)) return;
    this.retries.push(now);
    // eslint-disable-next-line no-console
    console.warn(`[${this.props.label ?? 'ui'}] remounted after an error (${this.retries.length})`);
    this.remount();
  }

  private remount = (): void => {
    // a new key: React builds the subtree from scratch, as F5 did, over the same store
    this.setState((s) => ({ failed: false, generation: s.generation + 1 }));
  };

  render(): ReactNode {
    if (this.state.failed) {
      if (this.props.recover) {
        return (
          <div className="frame-error" role="alert">
            <p>Не получилось показать разговор.</p>
            <button type="button" className="glass-btn" onClick={this.remount}>
              Показать ещё раз
            </button>
          </div>
        );
      }
      return this.props.fallback ?? null;
    }
    return <Fragment key={this.state.generation}>{this.props.children}</Fragment>;
  }
}
